import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDownCircle, ArrowUpCircle, BarChart3, Calculator, CalendarDays, ChevronRight, Crown, PenLine, PieChart, Users, Wallet } from 'lucide-react';
import { SkeletonStatCards, SkeletonTable } from '../../../components/common/SkeletonLoader';
import { canEditTaxonomy } from '../../../hooks/pipelineTracker/reportContract';
import { useAdSpend } from '../../../hooks/pipelineTracker/useAdSpend';
import { useMarketingReport, type MarketingMonth, type MarketingReportData } from '../../../hooks/pipelineTracker/useMarketingReport';
import {
  GST_RULE,
  dollarsToCents,
  entryProblem,
  exGstCents,
  type AdSpendEntry,
  type GstBasis,
} from '../../../hooks/pipelineTracker/adSpendRules';
import { ComingSoonList, Panel, Pill, Honesty, num, type ComingSoonItem } from './pipelineParts';
import { WarnState, shortCount } from './trackerUi';
import { Chip, DEFAULT_PERIOD_PRESETS, HouseStatCard, HouseStatGrid, PeriodBar, chartColour, hexRgb } from './trackerKit';
import { TrackerSection, useSectionAccent } from './TrackerSection';
import {
  COST_PER_LEAD_COLOUR,
  channelCents,
  channelColours,
  marketingSummary,
  monthsInRange,
  percent,
  periodChip,
  periodMonths,
  rangeLabel,
  spendMix,
  type MarketingPeriodKey,
  type MarketingSummary,
  type MonthRange,
} from './marketingData';
import { CostPerLeadChart, SpendByChannelChart } from './MarketingCharts';
import { Donut } from './SalesCharts';

/**
 * SalesTrekker Marketing tab.
 *
 * WHAT IS LIVE AND WHAT WAITS. Spend is entered here by the practice. Leads are dated in the Sales
 * export. So spend by month, leads created by month, and spend over leads are measurable today, and
 * that is the one section with figures. Every section the mockup draws per CAMPAIGN needs to know
 * which campaign produced each deal, and Salestrekker records no campaign on a deal yet, so none of
 * those shows a figure (founder decision, 28 Sep 2026). A campaign split estimated from the channel
 * totals would be read as a measurement. They are not drawn as cards: each is named in the tab's
 * "Coming when the data is available" list, with what it needs and who owes it (founder, 1 Oct 2026).
 *
 * NO RETURN ON SPEND HERE. Revenue is commission, and commission data sits outside the tracker's
 * isolation fence. The mockup's ROAS waits with the campaign sections rather than being computed
 * from a figure this tab cannot see.
 */

const CAMPAIGN_WHO = 'Evoke';
const CAMPAIGN_NEEDS = 'A campaign recorded on each deal in Salestrekker, so leads can be attributed to spend.';

/** Mockup order. Each shows no figure until the campaign field exists. */
export const CAMPAIGN_SECTIONS: ReadonlyArray<ComingSoonItem> = [
  'Marketing snapshot',
  'Campaign mix across the funnel',
  'Spend and efficiency by campaign',
  'Marketing leads by broker',
  'Cost per settled file by broker',
  'Campaign rollup',
  'Conversion by campaign',
].map((section) => ({ section, needs: CAMPAIGN_NEEDS, from: CAMPAIGN_WHO }));

const dollars = (cents: number): string =>
  (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 });

/**
 * Whole dollars for the spend card. A practice's spend is thousands, not millions, so the
 * abbreviated $1K the pipeline cards use would hide most of the figure.
 */
const wholeDollars = (cents: number): string =>
  (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

const monthName = (key: string): string => {
  const [y, m] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
};

/** The ad spend editor's accent outside a TrackerSection; inside one it wears the section's colour. */
const EDITOR_ACCENT = chartColour(3);

const CONTROL = 'rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none';

/**
 * The entry editor. Edits a local draft and saves the whole list once, so a half-typed month is
 * never written and the audit log records one change per save, not one per keystroke.
 *
 * Sits BELOW the figures it feeds and folds like Observed stage milestones: a year of entries is
 * dozens of rows, and drawn open at the top it pushed the one section with figures below the fold
 * (founder, 1 Oct 2026). Open on arrival only when nothing has been entered, since then the entry
 * form is the next thing to do.
 */
export const AdSpendEditor: React.FC<{
  entries: AdSpendEntry[];
  saving: boolean;
  onSave: (entries: AdSpendEntry[]) => Promise<{ ok: boolean; message?: string }>;
}> = ({ entries, saving, onSave }) => {
  const [draft, setDraft] = useState<AdSpendEntry[]>(entries);
  const [month, setMonth] = useState('');
  const [channel, setChannel] = useState('');
  const [amount, setAmount] = useState('');
  const [gst, setGst] = useState<GstBasis>('inc');
  const [problem, setProblem] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [open, setOpen] = useState(entries.length === 0);
  const accent = useSectionAccent() ?? EDITOR_ACCENT;
  useEffect(() => { setDraft(entries); }, [entries]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(entries);
  const sorted = [...draft].sort((a, b) => b.month.localeCompare(a.month) || a.channel.localeCompare(b.channel, 'en-AU'));

  const add = () => {
    const cents = dollarsToCents(amount);
    const entry: AdSpendEntry = { month, channel: channel.replace(/\s+/g, ' ').trim(), amountCents: cents ?? -1, gst };
    const why = cents === null ? 'Enter the amount in dollars and cents.' : entryProblem(entry, draft);
    setProblem(why);
    if (why) return;
    setDraft([...draft, entry]);
    setChannel(''); setAmount(''); setStatus(null);
  };

  return (
    <details
      className="group relative overflow-hidden rounded-xl border bg-gray-800/40"
      style={{ borderColor: `rgba(${hexRgb(accent)},0.3)` }}
      aria-label="Ad spend entry"
      data-panel-accent={accent}
      data-panel-title="Ad spend entry"
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      {/* The house panel look (trackerKit HousePanel), kept on <details> so the fold is native. */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[0.1875rem]" style={{ background: accent }} />
      <summary
        className="flex cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-1 px-5 pb-3.5 pt-4"
        style={{ background: `linear-gradient(180deg, rgba(${hexRgb(accent)},0.11), rgba(${hexRgb(accent)},0) 100%)` }}
      >
        <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-90" aria-hidden="true" />
        <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md"
          style={{ background: `rgba(${hexRgb(accent)},0.18)`, color: accent }}>
          <PenLine className="h-4 w-4" />
        </span>
        <h3 className="text-[0.9375rem] font-semibold text-gray-100">Ad spend entry</h3>
        <Pill tone="brand">Admins only</Pill>
        {entries.length > 0 && (
          <span className="text-xs text-gray-400">
            {num(entries.length)} {entries.length === 1 ? 'entry' : 'entries'}, click to {open ? 'close' : 'open'}
          </span>
        )}
      </summary>
      <div className="border-t border-gray-700/70 p-5">
      <p className="mb-4 text-xs leading-relaxed text-gray-400">
        Enter each month&apos;s spend per channel, as invoiced. The monthly figures above are calculated from these entries. Every save is audit-logged.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-gray-400">Month
          <input aria-label="Month" type="month" className={CONTROL} value={month} onChange={e => setMonth(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-400">Channel
          <input aria-label="Channel" type="text" maxLength={60} placeholder="e.g. Google" className={CONTROL} value={channel} onChange={e => setChannel(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-400">Amount ($)
          <input aria-label="Amount" type="text" inputMode="decimal" className={CONTROL} value={amount} onChange={e => setAmount(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-gray-400">GST
          <select aria-label="GST" className={CONTROL} value={gst} onChange={e => setGst(e.target.value as GstBasis)}>
            <option value="inc">Inc GST</option><option value="ex">Ex GST</option>
          </select>
        </label>
        <button type="button" className="rounded-md border border-blue-500/50 bg-blue-600/15 px-3 py-2 text-sm font-semibold text-blue-200 hover:bg-blue-600/25" onClick={add}>Add</button>
      </div>
      {problem && <p className="mt-2 text-sm text-amber-300" role="alert">{problem}</p>}

      {sorted.length === 0 ? (
        <p className="mt-4 text-sm text-gray-400">No spend entered yet.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm" aria-label="Ad spend entries">
            <thead className="border-b border-gray-700/70 text-[0.6875rem] uppercase tracking-[0.06em] text-gray-400"><tr>
              <th className="px-3 py-2">Month</th><th className="px-3 py-2">Channel</th>
              <th className="px-3 py-2 text-right">As entered</th><th className="px-3 py-2">GST</th>
              <th className="px-3 py-2 text-right">Ex GST</th><th className="px-3 py-2" />
            </tr></thead>
            <tbody className="divide-y divide-gray-700/60">
              {sorted.map(entry => (
                <tr key={`${entry.month}|${entry.channel}`} className="text-gray-300">
                  <td className="px-3 py-2">{monthName(entry.month)}</td>
                  <td className="px-3 py-2">{entry.channel}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{dollars(entry.amountCents)}</td>
                  <td className="px-3 py-2">{entry.gst === 'inc' ? 'Inc' : 'Ex'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{dollars(exGstCents(entry))}</td>
                  <td className="px-3 py-2 text-right">
                    <button type="button" className="text-xs text-gray-400 hover:text-red-300"
                      aria-label={`Remove ${entry.channel} ${entry.month}`}
                      onClick={() => setDraft(draft.filter(e => e !== entry))}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" disabled={!dirty || saving}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
          onClick={async () => { const out = await onSave(draft); setStatus(out.ok ? 'Saved.' : out.message ?? 'Not saved.'); }}>
          {saving ? 'Saving' : 'Save ad spend'}
        </button>
        {dirty && <button type="button" className="text-sm text-gray-400 hover:text-gray-200" onClick={() => { setDraft(entries); setProblem(null); }}>Discard changes</button>}
        {status && <span className="text-sm text-gray-300" role="status">{status}</span>}
      </div>
      <div className="mt-3"><Honesty>{GST_RULE}</Honesty></div>
      </div>
    </details>
  );
};

const STATUS_NOTE: Record<MarketingMonth['status'], string | null> = {
  complete: null,
  in_progress: 'Month in progress at the snapshot',
  after_snapshot: 'After the snapshot',
  no_snapshot: 'No Sales export yet',
};

/**
 * Six headline cards, each a sum or ratio of the month table below. Six, not five: five sat three
 * over two on a 14 inch screen (founder, 5 Oct 2026), and the sixth, the top channel, is the mix
 * panel's first line, so it adds no figure the tab does not already show.
 */
export const MarketingStatCards: React.FC<{ summary: MarketingSummary; topChannel?: { channel: string; cents: number; share: number } | null }> = ({
  summary, topChannel = null,
}) => (
  <HouseStatGrid count={6} testId="marketing-stat-cards">
    <HouseStatCard
      label="Total spend"
      value={wholeDollars(summary.spendCents)}
      sub={`Ex GST, ${num(summary.monthsWithSpend)} ${summary.monthsWithSpend === 1 ? 'month' : 'months'} with spend entered`}
      icon={Wallet}
      color={chartColour(0)}
    />
    <HouseStatCard
      label="Leads created"
      value={summary.leads === null ? '-' : shortCount(summary.leads)}
      sub={summary.leads === null ? 'No Sales export covers these months yet' : 'In those months, every source'}
      icon={Users}
      color={chartColour(2)}
    />
    <HouseStatCard
      label="Average cost per lead"
      value={summary.averageCostPerLeadCents === null ? '-' : dollars(summary.averageCostPerLeadCents)}
      sub={summary.costPerLeadMonths === 0
        ? 'Needs a complete month'
        : `Over ${num(summary.costPerLeadMonths)} complete ${summary.costPerLeadMonths === 1 ? 'month' : 'months'}`}
      info="Spend ex GST over every lead created, pooled across the complete months with spend and leads. Shown once a complete month has both."
      icon={Calculator}
      color={COST_PER_LEAD_COLOUR}
    />
    <HouseStatCard
      label="Lowest cost per lead"
      value={summary.lowest ? dollars(summary.lowest.cents) : '-'}
      sub={summary.lowest ? monthName(summary.lowest.month) : 'No complete month yet'}
      icon={ArrowDownCircle}
      color="#10b981"
      tone={summary.lowest ? 'good' : 'plain'}
    />
    <HouseStatCard
      label="Highest cost per lead"
      value={summary.highest ? dollars(summary.highest.cents) : '-'}
      sub={summary.highest ? monthName(summary.highest.month) : 'Needs two complete months'}
      icon={ArrowUpCircle}
      color={chartColour(1)}
      tone={summary.highest ? 'warn' : 'plain'}
    />
    <HouseStatCard
      label="Top channel"
      value={topChannel ? percent(topChannel.share) : '-'}
      sub={topChannel ? `${topChannel.channel}, ${wholeDollars(topChannel.cents)}` : 'No spend above $0'}
      info="The channel with the largest share of the spend in the period, ex GST."
      icon={Crown}
      color={chartColour(8)}
    />
  </HouseStatGrid>
);

const Legend: React.FC<{ channels: string[]; colours: Map<string, string> }> = ({ channels, colours }) => (
  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-gray-400" data-testid="channel-legend">
    {channels.map((c) => (
      <span key={c} className="inline-flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colours.get(c) }} />
        {c}
      </span>
    ))}
  </div>
);

/** Spend by channel, stacked by month: the month table's channel columns, drawn. */
export const SpendByChannelPanel: React.FC<{ months: MarketingMonth[]; channels: string[]; colours: Map<string, string> }> = ({ months, channels, colours }) => (
  <Panel title="Spend by channel, by month" icon={BarChart3} accent={chartColour(0)}
    blurb="Each month's spend ex GST, stacked by channel. Hover a column for each channel's spend and share of the month. Colour is fixed per channel across this tab.">
    <SpendByChannelChart months={months} channels={channels} colours={colours} />
    <Legend channels={channels} colours={colours} />
  </Panel>
);

/**
 * Every month's spend together, split by channel: a donut with the total in its centre, each
 * channel's dollars, share and how many of the period's months it ran in, then the period's channel
 * count, months with spend and average spend a month. It sits beside the cost per lead chart, so it
 * carries enough figures to fill that height rather than three rows over empty space.
 */
export const SpendMixPanel: React.FC<{ months: MarketingMonth[]; channels: string[]; colours: Map<string, string>; periodLabel?: string }> = ({
  months, channels, colours, periodLabel = 'All months',
}) => {
  const mix = spendMix(months, channels);
  const total = mix.reduce((sum, m) => sum + m.cents, 0);
  const spendMonths = months.filter((m) => m.spendExGstCents > 0);
  const activeMonths = (channel: string): number => spendMonths.filter((m) => channelCents(m, channel) > 0).length;
  return (
    <Panel title="Spend mix across the window" icon={PieChart} accent={chartColour(8)} blurb="Each channel's share of the spend entered in the period, ex GST.">
      {total === 0 ? (
        <p className="text-sm text-gray-400">No spend above $0 entered yet.</p>
      ) : (
        <>
          <div className="mb-3 flex items-baseline justify-between text-xs text-gray-400">
            <b className="font-semibold text-gray-200">{periodLabel}</b>
            <span className="tabular-nums">{dollars(total)} ex GST</span>
          </div>
          <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
            <div data-testid="spend-mix-donut">
              <Donut
                data={mix.map((m) => ({ label: m.channel, value: m.cents, colour: colours.get(m.channel) }))}
                format={(v: number | string) => dollars(Number(v))}
                centre={(
                  <span>
                    <span className="block text-[0.625rem] font-medium uppercase tracking-wider text-gray-400">Total</span>
                    <span className="tabular-nums">{wholeDollars(total)}</span>
                  </span>
                )}
              />
            </div>
            <ul className="w-full min-w-0 flex-1 space-y-3" aria-label="Spend by channel" data-testid="spend-mix-rows">
              {mix.map((m) => {
                const label = `${m.channel}: ${percent(m.share)} (${dollars(m.cents)})`;
                const active = activeMonths(m.channel);
                return (
                  <li key={m.channel} className="text-sm" aria-label={label}>
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: colours.get(m.channel) }} />
                      <span className="min-w-0 flex-1 truncate text-gray-200">{m.channel}</span>
                      <span className="tabular-nums text-gray-300">{dollars(m.cents)}</span>
                      <span className="w-10 text-right text-xs tabular-nums text-gray-400">{percent(m.share)}</span>
                    </div>
                    <div className="ml-[1.125rem] mt-1 h-1 overflow-hidden rounded-full bg-gray-900/70">
                      <div className="h-full rounded-full" style={{ width: `${m.share * 100}%`, background: colours.get(m.channel) }} />
                    </div>
                    <p className="ml-[1.125rem] mt-1 text-xs text-gray-400">
                      In {num(active)} of {num(spendMonths.length)} {spendMonths.length === 1 ? 'month' : 'months'}
                    </p>
                  </li>
                );
              })}
            </ul>
          </div>
          <dl className="mt-5 grid grid-cols-3 gap-3 border-t border-gray-700/60 pt-4" data-testid="spend-mix-figures">
            {[
              { k: 'Channels', v: num(mix.length) },
              { k: 'Months with spend', v: num(spendMonths.length) },
              { k: 'Average a month', v: spendMonths.length ? wholeDollars(Math.round(total / spendMonths.length)) : '-' },
            ].map(({ k, v }) => (
              <div key={k} className="min-w-0">
                <dt className="text-[0.6875rem] uppercase tracking-wider text-gray-400">{k}</dt>
                <dd className="mt-0.5 text-base font-semibold tabular-nums text-gray-100">{v}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </Panel>
  );
};

export const CostPerLeadPanel: React.FC<{ months: MarketingMonth[]; summary: MarketingSummary }> = ({ months, summary }) => (
  <Panel title="Cost per lead by month" icon={Calculator} accent={COST_PER_LEAD_COLOUR}
    blurb="Spend ex GST over every lead created that month. Complete months only; the dashed line is the average across them.">
    {summary.costPerLeadMonths === 0 ? (
      <p className="text-sm text-gray-400">No complete month with spend and leads yet, so there is no cost per lead to draw.</p>
    ) : (
      <CostPerLeadChart months={months} averageCents={summary.averageCostPerLeadCents} />
    )}
  </Panel>
);

/** Spend, leads created and blended cost per lead, one row per month with spend entered. */
export const SpendByMonthPanel: React.FC<{
  months: MarketingMonth[];
  channels: string[];
  asAtDate: string | null;
  missingCreatedDate: number;
  colours?: Map<string, string>;
  /** Said instead of the no-spend line when spend exists but none falls in the chosen period. */
  emptyNote?: string;
}> = ({ months, channels, asAtDate, missingCreatedDate, colours = channelColours(channels), emptyNote }) => {
  const maxSpend = Math.max(0, ...months.map(m => m.spendExGstCents));
  return (
    <Panel title="Spend, leads and cost per lead by month" icon={CalendarDays} accent={chartColour(5)}
      blurb={`Months with spend entered. Spend is ex GST. Leads are the deals created in each month in the Sales export${asAtDate ? ` as at ${asAtDate}` : ''}, from every source.`}>
      {months.length === 0 ? (
        <p className="text-sm text-gray-400">{emptyNote ?? 'No spend entered yet, so there is nothing to divide. Enter a month in Ad spend entry below to see it here.'}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" aria-label="Spend by month">
            <thead className="border-b border-gray-700/70 text-[0.6875rem] uppercase tracking-[0.06em] text-gray-400"><tr>
              <th className="px-3 py-2 font-semibold">Month</th>
              {channels.map(c => (
                <th key={c} className="px-3 py-2 text-right font-semibold">
                  <span className="inline-flex items-center gap-1.5">
                    <span aria-hidden="true" data-testid="channel-dot" className="h-2 w-2 rounded-sm" style={{ background: colours.get(c) }} />
                    {c}
                  </span>
                </th>
              ))}
              <th className="px-3 py-2 text-right font-semibold">Spend ex GST</th>
              <th className="px-3 py-2 text-right font-semibold">Leads created</th>
              <th className="px-3 py-2 text-right font-semibold">Cost per lead</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-700/60">
              {months.map(m => (
                <tr key={m.month} className="text-gray-300 transition-colors hover:bg-blue-500/[0.06]">
                  <td className="px-3 py-2.5 text-gray-200">{monthName(m.month)}
                    {STATUS_NOTE[m.status] && <span className="ml-2 text-xs text-amber-300">{STATUS_NOTE[m.status]}</span>}</td>
                  {channels.map(c => {
                    const hit = m.channels.find(x => x.channel === c);
                    return <td key={c} className="px-3 py-2.5 text-right tabular-nums">{hit ? dollars(hit.exGstCents) : '-'}</td>;
                  })}
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-100">
                    {dollars(m.spendExGstCents)}
                    {/* The month's spend against the biggest month, split by channel in the chart's colours. */}
                    <div className="ml-auto mt-1 flex h-1.5 w-24 overflow-hidden rounded-full bg-gray-900/70">
                      <div className="flex h-full" data-testid="spend-bar" style={{ width: `${maxSpend ? (m.spendExGstCents / maxSpend) * 100 : 0}%` }}>
                        {channels.filter(c => channelCents(m, c) > 0).map(c => (
                          <div key={c} className="h-full" style={{ flexGrow: channelCents(m, c), background: colours.get(c) }} />
                        ))}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{m.leadsCreated === null ? '-' : num(m.leadsCreated)}</td>
                  <td className="px-3 py-2.5 text-right font-semibold tabular-nums" style={m.costPerLeadCents === null ? undefined : { color: COST_PER_LEAD_COLOUR }}>
                    {m.costPerLeadCents === null ? '-' : dollars(m.costPerLeadCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3">
        <Honesty>
          Cost per lead is spend ex GST divided by every lead created that month, whatever its source, because no deal records which campaign produced it. Referrals and repeat clients are in the divisor, so this reads lower than a true cost per marketing lead. It is shown only for complete months: a part month divides a full month's spend by some of its leads.
          {missingCreatedDate > 0 && ` ${num(missingCreatedDate)} deals in the export have no valid creation date and are in no month.`}
        </Honesty>
      </div>
    </Panel>
  );
};

/** The Marketing period presets: ALL (every month with spend, the default) then the to-date set. */
export const MARKETING_PERIOD_PRESETS = [{ key: 'all', label: 'ALL' }, ...DEFAULT_PERIOD_PRESETS];

/**
 * The figures for the chosen months: cards, the two charts and the mix, then the month table they
 * are all drawn from. `range` null is every month with spend.
 */
export const MarketingFigures: React.FC<{ data: MarketingReportData; range?: MonthRange | null }> = ({ data, range = null }) => {
  const months = useMemo(() => monthsInRange(data.months, range), [data.months, range]);
  const colours = useMemo(() => channelColours(data.channels), [data.channels]);
  const summary = useMemo(() => marketingSummary(months), [months]);
  const topChannel = useMemo(() => spendMix(months, data.channels)[0] ?? null, [months, data.channels]);
  const label = range ? rangeLabel(range) : 'All months';
  const emptyNote = range && data.months.length > 0
    ? `No spend entered for ${label}. Choose ALL to see every month with spend.`
    : undefined;
  const table = <SpendByMonthPanel months={months} channels={data.channels} asAtDate={data.asAtDate} missingCreatedDate={data.missingCreatedDate} colours={colours} emptyNote={emptyNote} />;
  // Nothing in the period: the table says so in words. Six cards of dashes and two empty charts would not.
  if (months.length === 0) return table;
  return (
    <div className="space-y-5">
      <MarketingStatCards summary={summary} topChannel={topChannel} />
      <SpendByChannelPanel months={months} channels={data.channels} colours={colours} />
      <div className="grid gap-5 lg:grid-cols-2">
        <SpendMixPanel months={months} channels={data.channels} colours={colours} periodLabel={label} />
        <CostPerLeadPanel months={months} summary={summary} />
      </div>
      {table}
    </div>
  );
};

/**
 * The snapshot date a period counts from. The Sales snapshot when there is one; else the last
 * month with spend (the tab can show spend before any Sales export).
 */
const periodAnchor = (data: MarketingReportData): string => {
  if (data.asAtDate) return data.asAtDate;
  const last = [...data.months].map((m) => m.month).sort().pop();
  return last ? `${last}-01` : new Date().toISOString().slice(0, 10);
};

const MarketingTab: React.FC<{ companyId: string; role?: string }> = ({ companyId, role }) => {
  const canEdit = canEditTaxonomy(role);
  const adSpend = useAdSpend(companyId, canEdit);
  const [refreshKey, setRefreshKey] = useState(0);
  const { result, loading, error } = useMarketingReport(companyId, refreshKey);
  const data = result?.status === 'ok' ? result.data : null;
  const [period, setPeriod] = useState<MarketingPeriodKey>('all');
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null);
  const range = data ? periodMonths(period, periodAnchor(data), custom ?? undefined) : null;
  // CUSTOM before Apply keeps the previous months on screen: nothing changes on a half-chosen window.
  const shownRange = period === 'custom' && !custom ? null : range;
  const firstMonth = data && data.months.length > 0 ? [...data.months].map((m) => m.month).sort()[0] : null;
  const { save: saveAdSpend } = adSpend;
  const save = useCallback(async (entries: AdSpendEntry[]) => {
    const out = await saveAdSpend(entries);
    // Re-read the figures so the month table follows the entry without a page reload.
    if (out.ok) setRefreshKey(k => k + 1);
    return out;
  }, [saveAdSpend]);

  return (
    <div className="space-y-6">
      {/* First on the tab: it is the one section with figures. The entry it is calculated from
          follows, folded, so a long list of entries never pushes it below the fold. */}
      <TrackerSection
        id="mkt-cost-per-lead"
        step="1 · Cost per lead"
        title="What the marketing spend bought"
        description="Spend by channel, the leads created in the same months, and what each lead cost. Spend is ex GST; leads are from every source."
      >
        {loading ? <div className="space-y-5"><SkeletonStatCards count={5} columns={3} /><SkeletonTable rows={4} columns={5} /></div>
          : error ? <WarnState>{error}</WarnState>
          : data ? <>
            {data.adSpendWarning && <WarnState>{data.adSpendWarning}</WarnState>}
            {data.months.length > 0 && (
              <PeriodBar
                presets={MARKETING_PERIOD_PRESETS}
                selected={period}
                onSelect={(key) => setPeriod(key as MarketingPeriodKey)}
                onApplyCustom={(from, to) => setCustom({ from, to })}
                custom={custom ?? { from: firstMonth ? `${firstMonth}-01` : '', to: periodAnchor(data) }}
                trailing={<Chip tone="blue">{period === 'custom' && !custom ? 'Choose dates, then Apply' : periodChip(period, shownRange)}</Chip>}
              />
            )}
            <MarketingFigures data={data} range={shownRange} />
          </> : null}
      </TrackerSection>
      {canEdit && (
        <TrackerSection
          id="mkt-entry"
          step="2 · Entry"
          title="Record each month's spend"
          description="Admins only. Enter each month's spend per channel, as invoiced. Every save is audit-logged."
        >
          {adSpend.error
            ? <WarnState>{adSpend.error}</WarnState>
            : adSpend.ready && <AdSpendEditor entries={adSpend.entries} saving={adSpend.saving} onSave={save} />}
        </TrackerSection>
      )}
      <ComingSoonList items={CAMPAIGN_SECTIONS} />
    </div>
  );
};

export default MarketingTab;
